import { useEffect, useRef } from 'react';
import useAudioStore from '../store/audioStore';

/**
 * AudioProvider - Owns the SINGLE audio element for entire app
 * 
 * Architecture guarantees:
 * 1. Only ONE <audio> element exists
 * 2. Never remounts (created once on app mount)
 * 3. Persists across ALL route changes
 * 4. Event listeners attached ONCE
 * 
 * This provider creates the audio element and passes its ref to the store.
 * All playback logic lives in the store, not here.
 */
const AudioProvider = ({ children }) => {
  // SINGLE audio element - created once, never recreated
  const audioRef = useRef(new Audio());
  
  const { 
    setAudioRef, 
    updateTime, 
    handleEnded,
    status,
  } = useAudioStore();
  
  // Initialize audio ref in store (runs once)
  useEffect(() => {
    console.log('🎵 AudioProvider: Initializing SINGLE audio element');
    setAudioRef(audioRef);
    
    // For debugging: verify only ONE audio element exists
    if (typeof window !== 'undefined') {
      const audioElements = document.querySelectorAll('audio');
      console.log(`🔍 Audio elements in DOM: ${audioElements.length}`);
    }
  }, [setAudioRef]);
  
  // Setup event listeners ONCE - attached to single audio element
  useEffect(() => {
    const audio = audioRef.current;
    
    const onTimeUpdate = () => {
      updateTime(audio.currentTime, audio.duration);
    };
    
    const onLoadedMetadata = () => {
      updateTime(audio.currentTime, audio.duration);
    };
    
    const onEnded = () => {
      console.log('🎵 Audio ended, calling handleEnded()');
      handleEnded();
    };
    
    const onError = (e) => {
      console.error('❌ Audio error:', e);
      useAudioStore.setState({ status: 'idle' });
    };
    
    const onLoadStart = () => {
      console.log('🎵 Audio loading...');
    };
    
    const onCanPlay = () => {
      console.log('🎵 Audio ready to play');
    };
    
    // Attach listeners
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    audio.addEventListener('loadstart', onLoadStart);
    audio.addEventListener('canplay', onCanPlay);
    
    console.log('🎵 AudioProvider: Event listeners attached');
    
    // Cleanup
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('loadstart', onLoadStart);
      audio.removeEventListener('canplay', onCanPlay);
      console.log('🎵 AudioProvider: Event listeners removed');
    };
  }, [updateTime, handleEnded]);
  
  // Cleanup on app unmount (should rarely happen)
  useEffect(() => {
    const audio = audioRef.current;
    
    return () => {
      console.log('🎵 AudioProvider: Cleaning up audio element');
      audio.pause();
      audio.src = '';
    };
  }, []);
  
  // Save state periodically when playing
  useEffect(() => {
    let interval;
    
    if (status === 'playing') {
      interval = setInterval(() => {
        useAudioStore.getState().saveToLocalStorage();
      }, 5000); // Save every 5 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [status]);
  
  return children;
};

export default AudioProvider;

